import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { Observable, forkJoin, from, map, switchMap } from 'rxjs';
import {
  AUTH_PROVIDER_PORT,
  AuthProviderPort,
} from '../../../authentication/application/ports/auth-provider.port';
import { encryptWithPublicKey } from '../../../authentication/provider/crypto/rsa-encryptor';
import { StandardResponse } from '../../../common/dto/standar-response.dto';
import { SKIP_ERROR_TOAST } from '../../../common/interceptors/skip-error-toast.token';
import { API_BASE_URL } from '../../../common/tokens/api-base-url.token';
import { ChangePasswordRequest } from '../../application/entities/change-password-request.entity';
import { UpdateProfileRequest } from '../../application/entities/update-profile-request.entity';
import { UserProfile } from '../../application/entities/user-profile.entity';
import { ProfileProviderPort } from '../../application/ports/profile-provider.port';
import { ChangePasswordRequestDto } from '../dto/change-password-request.dto';
import { GetProfileResponseDto } from '../dto/get-profile-response.dto';
import { ProfileUserDto } from '../dto/profile-user.dto';
import { toUpdateProfileRequestDto, toUserProfile } from '../mappers/profile.mapper';

const PROFILE_CONTEXT = new HttpContext().set(SKIP_ERROR_TOAST, true);

@Injectable({ providedIn: 'root' })
export class ProfileProviderService implements ProfileProviderPort {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  constructor(
    @Inject(AUTH_PROVIDER_PORT) private readonly authProvider: AuthProviderPort,
  ) {}

  getProfile(): Observable<UserProfile> {
    return this.http
      .get<StandardResponse<GetProfileResponseDto>>(`${this.baseUrl}auth/me`)
      .pipe(map((response) => toUserProfile(response.data!.user, response.data!.organization)));
  }

  updateProfile(request: UpdateProfileRequest): Observable<UserProfile> {
    const body = toUpdateProfileRequestDto(request);
    return this.http
      .put<StandardResponse<ProfileUserDto>>(`${this.baseUrl}auth/me`, body)
      .pipe(map((response) => toUserProfile(response.data!)));
  }

  uploadAvatar(file: File): Observable<UserProfile> {
    const formData = new FormData();
    formData.append('avatar', file);
    return this.http
      .post<StandardResponse<ProfileUserDto>>(`${this.baseUrl}auth/me/avatar`, formData)
      .pipe(map((response) => toUserProfile(response.data!)));
  }

  removeAvatar(): Observable<UserProfile> {
    return this.http
      .delete<StandardResponse<ProfileUserDto>>(`${this.baseUrl}auth/me/avatar`)
      .pipe(map((response) => toUserProfile(response.data!)));
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.authProvider.getPublicKey().pipe(
      switchMap(({ publicKey }) =>
        forkJoin({
          currentPassword: from(encryptWithPublicKey(publicKey, request.currentPassword)),
          newPassword: from(encryptWithPublicKey(publicKey, request.newPassword)),
          confirmPassword: from(encryptWithPublicKey(publicKey, request.confirmPassword)),
        }),
      ),
      switchMap((encrypted: ChangePasswordRequestDto) =>
        this.http
          .post<StandardResponse<null>>(`${this.baseUrl}auth/change-password`, encrypted, {
            context: PROFILE_CONTEXT,
          })
          .pipe(map(() => void 0)),
      ),
    );
  }
}
